import React from 'react';

import { Translate } from 'react-localize-redux';
import { connect } from 'react-redux';

import styled from 'styled-components';

// Components
import NewTour from './new-tour';

import Box from '@components/layout/box';
import Typography from '@components/basics/typography';
import TourItem from './tour-item';
import { AnimatePresence } from 'framer-motion';
import AppState from '@webapp/store/state';
import { Dispatch } from 'redux';
import {
  AddTourEvent,
  GetTourEvents,
  UpdateTourEvent,
  DeleteTourEvent,
} from '@webapp/store/traclink/action-creators';
import { TourEvent } from '@common/types/traclink/tour-event';
import { NewTourEvent } from '@common/types/traclink/new-tour-event';

const mapStateToProps = (state: AppState) => ({
  tourEvents: state.traclink.tourEvents,
  // TODO integrate tours error handling
  tourEventsError: state.traclink.tourEventsError,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getTourEvents: (ownerId: string) => dispatch(new GetTourEvents(ownerId)),
  addTourEvent: (newTourEvent: NewTourEvent) =>
    dispatch(new AddTourEvent(newTourEvent)),
  updateTourEvent: (tourEvent: TourEvent) =>
    dispatch(new UpdateTourEvent(tourEvent)),
  deleteTourEvent: (ownerId: string, tourId: string) =>
    dispatch(new DeleteTourEvent(ownerId, tourId)),
});

interface ArtistTourProps {
  className?: string;
  artistId: string;
  tourEvents: TourEvent[];
  tourEventsError?: string | null;
  getTourEvents: (ownerId: string) => void;
  addTourEvent: (newTourEvent: NewTourEvent) => void;
  updateTourEvent: (tourEvent: TourEvent) => void;
  deleteTourEvent: (ownerId: string, tourId: string) => void;
}

const ArtistTour = ({
  className,
  tourEvents,
  artistId,
  getTourEvents,
  addTourEvent,
  updateTourEvent,
  deleteTourEvent,
}: ArtistTourProps) => {
  const [showNewTour, setShowNewTour] = React.useState<boolean>(false);
  const [editingTourId, setEditingTourId] = React.useState<string | null>();
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (artistId) {
      getTourEvents(artistId);
    }
  }, [artistId, getTourEvents]);

  React.useEffect(() => {
    if (tourEvents.length) {
      setLoading(false);
      setShowNewTour(false);
      setEditingTourId(null);
    }
  }, [tourEvents]);

  const handleEditEvent = (tourEvent: TourEvent) => {
    updateTourEvent(tourEvent);
    setLoading(true);
  };

  // TODO: DEVTEAM-845 - Integrate handleDeleteEvent
  const handleDeleteEvent = (tourId: string) => {
    deleteTourEvent(artistId, tourId);
  };

  // TODO: DEVTEAM-845 - Integrate handleAddEvent
  const handleAddEvent = (newTourEvent: Omit<NewTourEvent, 'ownerId'>) => {
    if (artistId) {
      addTourEvent({
        ...newTourEvent,
        ownerId: artistId,
      });
      setLoading(true);

      return;
    }
    // TODO fix not artistId
    return;
  };

  const handleStartEditEvent = (tourId: string) => {
    setEditingTourId(tourId);
  };

  const handleCancelEditEvent = () => {
    setEditingTourId(null);
  };
  if (!tourEvents) return null;

  return (
    <Box className={className || ''}>
      <AnimatePresence exitBeforeEnter>
        {!showNewTour ? (
          <NewDateInput onClick={() => setShowNewTour(true)}>
            <Typography className="label">
              <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.TOUR.INPUT.NEW.LABEL" />
            </Typography>
          </NewDateInput>
        ) : (
          <NewTour
            loading={loading}
            onAdd={handleAddEvent}
            onCancel={() => setShowNewTour(false)}
          />
        )}
      </AnimatePresence>
      {!!tourEvents.length && (
        <ToursWrapper>
          {tourEvents.map((tour) => (
            <TourItem
              key={tour.id}
              tour={tour}
              loading={loading}
              isEdit={editingTourId === tour.id}
              onEditEvent={handleEditEvent}
              onDeleteEvent={handleDeleteEvent}
              onStartEditEvent={handleStartEditEvent}
              onCancelEditEvent={handleCancelEditEvent}
            />
          ))}
        </ToursWrapper>
      )}
    </Box>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(ArtistTour);

const NewDateInput = styled.div`
  background-color: ${({ theme }) => theme.colors.inputBackground};
  border-radius: 4px;
  min-height: 48px;
  padding: 16px 12px 16px 9px;
  cursor: pointer;

  .label {
    font-size: ${({ theme }) => theme.typography.span};
    font-weight: 700;
    line-height: 24px;
    color: ${({ theme }) => theme.colors.secondaryDark};

    ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
      font-size: ${({ theme }) => theme.typography.h4};
    }
  }

  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    min-height: 60px;
    padding: 22px 18px 22px 15px;
  }
`;

const ToursWrapper = styled.div`
  display: grid;
  grid-gap: 10px;
  margin-top: 10px;
`;
